import React, { useState, useRef, useCallback, useEffect } from "react";
import { safeParse } from "../../../helpers/helper";
import { useSelector } from "react-redux";

/**
 * ProductPicker — Tailwind CSS version
 *
 * Props:
 *  - products       {Array}    Controlled product list from parent
 *  - loading        {boolean}  Shows spinner at bottom while fetching
 *  - hasMore        {boolean}  Hides loader when all data is loaded
 *  - onSearch       {(query: string) => void}          Debounced search callback
 *  - onLoadMore     {() => void}                       Infinite scroll callback
 *  - onConfirm      {(selectedIds: number[]) => void}  Confirm callback
 *  - listHeight     {string}   Tailwind max-h class (default: "max-h-[520px]")
 *  - confirmText    {string}   Confirm button label (default: "Confirm")
 *  - maxSelection   {number}   Max selectable items (default: 120)
 *
 * Product shape: { id, name, category, price, imageUrl, stock }
 * stock: "In stock" | "Low stock" | "Out of stock"
 */


const Spinner = () => (
  <svg
    className="animate-spin h-4 w-4 text-blue-500"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
  </svg>
);

const Toast = ({ message, onClose }) => {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [message, onClose]);

  if (!message) return null;

  return (
    <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg px-3.5 py-2.5 mb-2 shadow-sm">
      <svg className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      </svg>
      <span>{message}</span>
    </div>
  );
};

const Checkbox = ({ checked, indeterminate, disabled, onChange }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate;
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      className={[
        "w-4 h-4 rounded border-gray-300 text-blue-600 shrink-0",
        "focus:ring-2 focus:ring-blue-500 focus:ring-offset-0",
        "accent-blue-600 cursor-pointer",
        disabled ? "opacity-40 cursor-not-allowed" : "",
      ].join(" ")}
    />
  );
};

const ProductPageRentalPicker = ({
  products = [],
  loading = false,
  hasMore = true,
  selectedProducts = [],
  onSearch,
  onLoadMore,
  onConfirm,
  listHeight = "max-h-[520px]",
  confirmText = "Confirm",
  maxSelection = 120,
}) => {
const types = useSelector((state) => state.workspace.types || {});
  const [selectedIds, setSelectedIds] = useState(new Set(selectedProducts || []));
  const [searchValue, setSearchValue] = useState("");
  const [toast, setToast] = useState("");
  const listRef = useRef(null);
  const searchTimer = useRef(null);

  // ── Infinite scroll ──────────────────────────────────────────────────────
  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || loading || !hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      onLoadMore?.();
    }
  }, [loading, hasMore, onLoadMore]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // ── Search ───────────────────────────────────────────────────────────────
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchValue(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => onSearch?.(val.trim()), 300);
  };

  const clearSearch = () => {
    setSearchValue("");
    clearTimeout(searchTimer.current);
    onSearch?.("");
  };

  // ── Selection ────────────────────────────────────────────────────────────
  const toggleItem = useCallback(
    (product) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(product.id)) {
          next.delete(product.id);
        } else {
          if (next.size >= maxSelection) {
            setToast(
              `Maximum ${maxSelection} items allowed. Deselect some to add more.`
            );
            return prev;
          }
          next.add(product.id);
        }
        return next;
      });
    },
    [maxSelection]
  );

  const allVisibleSelected =
    products.length > 0 && products.every((p) => selectedIds.has(p.id));
  const someVisibleSelected =
    !allVisibleSelected && products.some((p) => selectedIds.has(p.id));

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const toAdd = products.filter((p) => !selectedIds.has(p.id)).map((p) => p.id);
      const available = maxSelection - selectedIds.size;

      if (toAdd.length > available) {
        setToast(
          `Only ${available} more item${available !== 1 ? "s" : ""} can be selected (max ${maxSelection}). Deselect some first.`
        );
        if (available <= 0) return;
        const next = new Set(selectedIds);
        toAdd.slice(0, available).forEach((id) => next.add(id));
        setSelectedIds(next);
      } else {
        const next = new Set(selectedIds);
        toAdd.forEach((id) => next.add(id));
        setSelectedIds(next);
      }
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        products.forEach((p) => next.delete(p.id));
        return next;
      });
    }
  };

  const handleConfirm = () => onConfirm?.(Array.from(selectedIds));

  const selectedCount = selectedIds.size;
  const remaining = maxSelection - selectedCount;
  const atLimit = selectedCount >= maxSelection;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="w-full font-sans">
      <Toast message={toast} onClose={() => setToast("")} />

      {/* Top bar */}
      <div className="bg-white border border-gray-200 rounded-xl px-4 py-2.5 mb-2 flex items-center gap-3 flex-wrap">

        {/* Select all */}
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <Checkbox
            checked={allVisibleSelected}
            indeterminate={someVisibleSelected}
            onChange={handleSelectAll}
          />
          <span className="text-sm font-medium text-gray-700">Select all</span>
        </label>

        <div className="w-px h-4 bg-gray-200" />

        {/* Count */}
        <span
          className={[
            "text-xs font-medium tabular-nums",
            atLimit ? "text-red-500" : "text-gray-500",
          ].join(" ")}
          title={atLimit ? "Maximum reached" : `${remaining} more can be selected`}
        >
          {selectedCount}
          <span className="text-gray-300 mx-0.5">/</span>
          {maxSelection}
        </span>

        {/* Search */}
        <div className="flex-1 min-w-[160px] max-w-[280px] relative">
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <circle cx="11" cy="11" r="8" /><path strokeLinecap="round" d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            value={searchValue}
            onChange={handleSearchChange}
            placeholder="Search products…"
            className="w-full pl-8 pr-7 py-1.5 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
          />
          {searchValue && (
            <button
              onClick={clearSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Confirm button */}
        <button
          onClick={handleConfirm}
          disabled={selectedCount === 0}
          className={[
            "ml-auto flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-all",
            selectedCount === 0
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700 active:scale-95 text-white shadow-sm",
          ].join(" ")}
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          {confirmText}
          {selectedCount > 0 && (
            <span className="bg-white/25 text-white text-xs font-semibold rounded-full px-1.5 py-0.5 leading-none">
              {selectedCount}
            </span>
          )}
        </button>
      </div>

      {/* List */}
      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
        <div
          ref={listRef}
          className={`${listHeight} overflow-y-auto divide-y divide-gray-50`}
        >
          {products.length === 0 && !loading ? (
            <div className="flex flex-col items-center justify-center h-48 gap-2 text-gray-400">
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0H4" />
              </svg>
              <span className="text-sm">No products found</span>
            </div>
          ) : (
            products.map((product) => {
              const isSelected = selectedIds.has(product.id);
              const isDisabled = !isSelected && atLimit;

              return (
                <div
                  key={product.id}
                  onClick={() => !isDisabled && toggleItem(product)}
                  className={[
                    "flex items-center gap-3 px-4 py-2.5 transition-colors",
                    isSelected
                      ? "bg-blue-50"
                      : isDisabled
                      ? "bg-white opacity-50 cursor-not-allowed"
                      : "bg-white hover:bg-gray-50 cursor-pointer",
                  ].join(" ")}
                >
                  <Checkbox
                    checked={isSelected}
                    disabled={isDisabled}
                    onChange={() => toggleItem(product)}
                  />

                  {/* Image */}
                  { product.images &&
                  <img
                    src={safeParse(product.images)?.[0]}
                    alt={product.name}
                    className="w-11 h-11 rounded-lg object-cover border border-gray-100 shrink-0 bg-gray-50"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                }

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="flex items-center gap-2 text-sm font-medium truncate">
                        <span className="text-gray-800">{product.name}</span>

                        <span className="text-gray-400 text-xs leading-none">
                            •
                        </span>

                        <span className="text-gray-400">
                            {types[product.type]?.name}
                        </span>
                        </p>
                    <p className="text-xs text-gray-400 mt-0.5">{product.description}</p>
                  </div>

                  {/* Price + stock */}
                  <div className="text-right shrink-0">
                    <p className="text-sm font-semibold text-gray-800">
                      {'Rent for'} - {Number(product.defaultAmount).toLocaleString("en-IN")}
                    </p>
                    <span
                      className={[
                        "inline-block mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ring-1 ring-inset",
                      ].join(" ")}
                    >
                      {'Total Units'} - {product.totalUnits}
                    </span>
                  </div>
                </div>
              );
            })
          )}

          {/* Infinite scroll loader */}
          {loading && (
            <div className="flex items-center justify-center gap-2 py-4 text-gray-400">
              <Spinner />
              <span className="text-xs">Loading more…</span>
            </div>
          )}

          {!hasMore && products.length > 0 && (
            <p className="text-center text-xs text-gray-300 py-3">
              All {products.length} products loaded
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductPageRentalPicker;


